// Chapter 27 - Functional Behavior Assessment
// Source: Cooper, Heron, & Heward (3rd ed.), Chapter 27 (Peterson & Neef).

import type { ChapterModule } from "./types";

const chapter27Module: ChapterModule = {
  meta: {
    id: "27",
    number: 27,
    title: "Functional Behavior Assessment",
    subtitle: "Indirect, descriptive, and experimental analysis",
    part: "Part 10 - Functional Assessment",
    estMinutes: 80,
    bacb: ["F-5", "F-6", "F-7", "F-8", "F-9"],
  },
  curated: true,
  overview: [
    "Chapter 27 (Peterson & Neef) covers FUNCTIONAL BEHAVIOR ASSESSMENT (FBA): the assessment process that identifies the function a behavior serves for a person. FBA enables empirically based hypotheses about why problem behaviors occur, pointing to effective interventions.",
    "Three FBA method families: FUNCTIONAL (experimental) ANALYSIS, DESCRIPTIVE ASSESSMENT, and INDIRECT ASSESSMENT - on a continuum from easiest/least precise to most rigorous/most precise. The chapter outlines the four-step FBA process (gather, hypothesize, test, intervene) and emphasizes that replacement behaviors should be FUNCTIONALLY EQUIVALENT to the problem behavior.",
  ],
  objectives: [
    { id: "F-5", text: "Describe common functions of problem behavior." },
    { id: "F-6", text: "Conduct a descriptive assessment of problem behavior." },
    { id: "F-7", text: "Conduct a functional analysis of problem behavior." },
    { id: "F-8", text: "Interpret functional assessment data." },
    { id: "F-9", text: "Develop function-based interventions based on FBA results." },
  ],
  coreConcepts: [
    {
      title: "Function vs topography",
      body: [
        "Many problem behaviors are learned and maintained by positive, negative, and/or automatic reinforcement. Topography reveals little about the maintaining contingency. Identifying the FUNCTION of a behavior tells the analyst what conditions to alter to change the behavior.",
      ],
    },
    {
      title: "Why FBA matters",
      body: [
        "FBA can lead to effective interventions in three ways: (a) identify antecedent variables to alter to prevent problem behavior, (b) identify reinforcement contingencies to alter so the behavior no longer receives reinforcement, and (c) identify reinforcers for alternative replacement behaviors. FBA reduces reliance on default technologies (increasingly intrusive, coercive, punishment-based interventions) and increases the use of reinforcement-based interventions.",
      ],
    },
    {
      title: "Three FBA method families",
      body: [
        "(1) FUNCTIONAL (EXPERIMENTAL) ANALYSIS: systematically manipulate environmental events thought to maintain problem behavior within an experimental design. Yields clearest demonstration of maintaining variables; requires expertise. (2) DESCRIPTIVE ASSESSMENT: observe the behavior in relation to non-arranged events (ABC continuous recording, narrative ABC, scatterplot). Easier; harder to parse contingencies. (3) INDIRECT ASSESSMENT: structured interviews, checklists, rating scales, questionnaires from people familiar with the individual. Easy but limited in accuracy; best for hypothesis formulation.",
      ],
    },
    {
      title: "Functional analysis variations",
      body: [
        "Variations include brief functional analysis, functional analysis in natural settings, trial-based functional analysis, synthesized functional analysis, latency-based functional analysis, and analyzing precursor behaviors.",
      ],
    },
    {
      title: "Four-step FBA process",
      body: [
        "(1) Gather information via indirect and descriptive assessment. (2) Interpret information and formulate hypotheses about the purpose of problem behavior. (3) Test hypotheses using functional analysis. (4) Develop intervention options based on the function of the problem behavior.",
      ],
    },
    {
      title: "Functional equivalence in replacement behaviors",
      body: [
        "When teaching an alternative behavior as a replacement, the replacement should be FUNCTIONALLY EQUIVALENT to the problem behavior - it should produce the SAME reinforcers that previously maintained the problem behavior.",
      ],
    },
    {
      title: "Common functions of problem behavior",
      body: [
        "Most function categories: ATTENTION (positive social reinforcement), TANGIBLES (positive material reinforcement), ESCAPE/AVOIDANCE (negative reinforcement from demands/aversive stimuli), and AUTOMATIC (sensory consequences from the behavior itself).",
      ],
    },
  ],
  glossary: [
    { term: "Functional behavior assessment (FBA)", definition: "An assessment process that identifies the function a behavior serves for a person.", sourceHint: "Functional behavior assessment enables behavior analysts to make empirically based hypotheses for why problem behaviors occur." },
    { term: "Function of behavior", definition: "The contingencies of reinforcement that maintain the behavior (e.g., attention, escape, tangible, automatic).", sourceHint: "Many problem behaviors are learned and maintained by positive, negative, and/or automatic reinforcement. In this respect, problem behavior can be said to have a 'function'." },
    { term: "Functional (experimental) analysis", definition: "FBA method that systematically manipulates environmental events thought to maintain problem behavior within an experimental design.", sourceHint: "Functional analysis involves systematically manipulating environmental events thought to maintain problem behavior within an experimental design." },
    { term: "Descriptive assessment", definition: "FBA method involving observation of the behavior in relation to non-arranged events (ABC, scatterplot).", sourceHint: "Descriptive assessment involves observation of the problem behavior in relation to events that are not arranged in a systematic manner and includes ABC recording (both continuous and narrative) and scatterplot recording." },
    { term: "Indirect assessment", definition: "FBA method using structured interviews, checklists, rating scales, or questionnaires.", sourceHint: "Indirect functional assessment methods use structured interviews, checklists, rating scales, or questionnaires to obtain information from persons who are familiar with the individual exhibiting the problem behavior." },
    { term: "Brief functional analysis", definition: "Time-limited functional analysis suited to outpatient or single-session contexts.", sourceHint: "Several variations of functional analysis procedures have been developed... including brief functional analysis." },
    { term: "Trial-based functional analysis", definition: "Functional analysis embedded in naturally occurring instructional or activity trials.", sourceHint: "trial-based functional analysis." },
    { term: "Latency-based functional analysis", definition: "Functional analysis variant that uses response latency rather than response rate as the dependent measure.", sourceHint: "latency-based functional analysis." },
    { term: "Precursor behavior analysis", definition: "Analyzing behaviors that reliably precede dangerous behaviors so safer responses can serve as the dependent measure.", sourceHint: "analyzing precursor behaviors." },
    { term: "Functionally equivalent replacement behavior", definition: "Replacement behavior that produces the same reinforcer that maintained the problem behavior.", sourceHint: "the replacement behavior should be functionally equivalent to the problem behavior (i.e., the replacement behavior should produce the same reinforcers that previously maintained the problem behavior)." },
    { term: "Default technologies", definition: "Increasingly intrusive, coercive, and punishment-based interventions used when function-based options are not available.", sourceHint: "FBA can decrease reliance on default technologies (increasingly intrusive, coercive, and punishment-based interventions) and contribute to more effective interventions." },
  ],
  procedures: [
    {
      title: "Conduct a complete FBA (four-step process)",
      intro: "Move from indirect to functional analysis as needed.",
      steps: [
        { title: "Step 1 - Indirect & descriptive", detail: "Use interviews, rating scales, and ABC recording / scatterplot in the natural setting." },
        { title: "Step 2 - Formulate hypotheses", detail: "Translate observations into testable function hypotheses (attention, escape, tangible, automatic)." },
        { title: "Step 3 - Functional analysis", detail: "Test hypotheses by systematically manipulating antecedents and consequences in controlled conditions." },
        { title: "Step 4 - Intervention", detail: "Design function-based intervention with extinction for the problem behavior and a functionally equivalent replacement." },
      ],
    },
    {
      title: "Pick the right FBA method",
      intro: "Trade-off ease, accuracy, and ethical/practical constraints.",
      steps: [
        { title: "Start with indirect", detail: "Use interviews and rating scales to scope possibilities." },
        { title: "Add descriptive", detail: "ABC recording or scatterplot to map natural-environment correlations." },
        { title: "Use functional analysis when feasible", detail: "Experimental manipulation is the gold standard when expertise and resources allow." },
        { title: "Consider variations", detail: "Brief functional analysis, trial-based, latency-based, or precursor analysis for tricky cases." },
      ],
    },
    {
      title: "Plan a function-based intervention",
      intro: "Match procedure to function.",
      steps: [
        { title: "Confirm function", detail: "Cross-check FBA results across methods where possible." },
        { title: "Identify replacement behavior", detail: "Pick a functionally equivalent response that produces the same reinforcer." },
        { title: "Plan extinction", detail: "Decide whether and how to withhold the maintaining reinforcer for the problem behavior." },
        { title: "Add antecedent supports", detail: "NCR, high-p sequences, environmental enrichment - all selected by function." },
        { title: "Train, measure, revise", detail: "Train implementers, monitor data, iterate based on FBA findings." },
      ],
    },
  ],
  examples: [
    { scenario: "A learner whose self-injury occurs almost exclusively during difficult demands.",
      application: "Likely escape-maintained. FBA hypothesis: negative reinforcement. Intervention: escape extinction + FCT.",
      sourceHint: "FBA can lead to effective interventions in at least three ways: (a) It can identify antecedent variables that can be altered to prevent problem behavior, (b) it can identify reinforcement contingencies that can be altered so that problem behavior no longer receives reinforcement, and (c) it can help identify reinforcers for alternative replacement behaviors." },
    { scenario: "A child engages in tantrums most often when parents are talking to siblings; when parents respond, tantrums end.",
      application: "Attention-maintained (positive social reinforcement). FBA confirms via demand-attention-alone conditions; intervention uses DRA, NCR attention, and extinction.",
      sourceHint: "Identifying the conditions that account for a behavior (its function) suggests what conditions need to be altered to change the behavior." },
    { scenario: "A learner engages in vocal stereotypy across all settings, including alone.",
      application: "Likely automatic positive reinforcement. Sensory extinction (e.g., headphones) plus competing reinforcers; FBA confirms when behavior persists in alone conditions.",
      sourceHint: "Many problem behaviors are learned and maintained by positive, negative, and/or automatic reinforcement." },
    { scenario: "An FBA includes scatterplot showing problem behavior peaks at 10-11 AM and ABC notes suggesting demands precede most occurrences.",
      application: "Descriptive assessment supports a hypothesis. Functional analysis is the next step to test the escape function experimentally.",
      sourceHint: "Descriptive assessment involves observation of the problem behavior in relation to events that are not arranged in a systematic manner and includes ABC recording (both continuous and narrative) and scatterplot recording." },
  ],
  comparisons: [
    {
      title: "Indirect vs Descriptive vs Functional analysis",
      left:  { label: "Indirect", points: [
        "Interviews, rating scales, questionnaires.",
        "Easiest; lowest precision.",
        "Best for hypothesis formulation.",
      ]},
      right: { label: "Descriptive / Functional analysis", points: [
        "Descriptive: ABC / scatterplot from natural observation.",
        "Functional analysis: experimental manipulation; highest precision.",
        "Functional analysis is the gold standard.",
      ]},
    },
    {
      title: "Standard FA vs Brief / Trial-based FA",
      left:  { label: "Standard FA", points: [
        "Multiple conditions over multiple sessions.",
        "Strong control; resource-intensive.",
      ]},
      right: { label: "Brief / Trial-based", points: [
        "Time-limited; embedded in natural routines.",
        "Less control but practical in many settings.",
      ]},
    },
    {
      title: "Topography-based vs Function-based intervention",
      left:  { label: "Topography-based", points: [
        "Targets the FORM (e.g., suppress all yelling).",
        "Misses function; often less durable.",
      ]},
      right: { label: "Function-based (FBA-driven)", points: [
        "Targets the maintaining contingency.",
        "Includes functionally equivalent replacement.",
        "More effective and ethical.",
      ]},
    },
  ],
  misconceptions: [
    {
      myth: "The form of a behavior tells you why it occurs.",
      reality: "Topography reveals little about function. FBA is required to identify the maintaining contingency.",
      sourceHint: "The topography, or form, of a behavior often reveals little useful information about the conditions that account for it.",
    },
    {
      myth: "Indirect assessment is sufficient on its own.",
      reality: "Indirect assessments are easy and useful for hypothesis formulation; they are limited in accuracy. Hypotheses almost always require further testing.",
      sourceHint: "These forms of FBA are easy to conduct, but they are limited in their accuracy. As such, they are probably best reserved for hypothesis formulation. Further assessment of these hypotheses is almost always necessary.",
    },
    {
      myth: "Functional analysis is dangerous and should be avoided.",
      reality: "Functional analysis is the gold standard for identifying maintaining variables and yields clear demonstrations of control. Brief, trial-based, latency-based, and precursor-behavior variants address safety and practicality concerns.",
      sourceHint: "Several variations of functional analysis procedures have been developed to adapt to a variety of situations and contexts, including brief functional analysis, conducting functional analyses in natural settings, trial-based functional analysis, synthesized functional analysis, latency-based functional analysis, and analyzing precursor behaviors.",
    },
    {
      myth: "Replacement behaviors only need to be appropriate.",
      reality: "Replacement behaviors must be FUNCTIONALLY EQUIVALENT - they must produce the same reinforcers that maintained the problem behavior - or they will not compete with it.",
      sourceHint: "the replacement behavior should be functionally equivalent to the problem behavior (i.e., the replacement behavior should produce the same reinforcers that previously maintained the problem behavior).",
    },
    {
      myth: "If FBA is unclear, default to punishment.",
      reality: "FBA reduces reliance on default technologies (intrusive, coercive, punishment-based). When function is unclear, refine the FBA with additional methods before escalating intervention intensity.",
      sourceHint: "FBA can decrease reliance on default technologies (increasingly intrusive, coercive, and punishment-based interventions).",
    },
  ],
  examChecklist: [
    "Define FBA and describe its three method families.",
    "Explain why topography is not a sufficient guide to intervention.",
    "List three ways FBA can lead to effective interventions.",
    "Describe the four-step FBA process.",
    "Name at least three variations of functional analysis.",
    "Distinguish descriptive ABC recording from a scatterplot.",
    "Explain functional equivalence in replacement behaviors.",
    "Name four common functions of problem behavior.",
    "Explain why indirect assessment is best for hypothesis formulation rather than final conclusions.",
  ],
  flashcardTerms: [
    "Functional behavior assessment (FBA)", "Function of behavior",
    "Functional (experimental) analysis",
    "Descriptive assessment", "Indirect assessment",
    "Brief functional analysis", "Trial-based functional analysis",
    "Latency-based functional analysis", "Precursor behavior analysis",
    "Functionally equivalent replacement behavior", "Default technologies",
  ],
  quiz: [
    {
      id: "Q1",
      question: "Which FBA method involves SYSTEMATIC MANIPULATION of environmental events in an experimental design?",
      options: [
        { label: "A", text: "Indirect assessment" },
        { label: "B", text: "Descriptive assessment" },
        { label: "C", text: "Functional analysis" },
        { label: "D", text: "Scatterplot recording" },
      ],
      answer: "C",
      explanation: "Functional (experimental) analysis manipulates antecedents and consequences in an experimental design to demonstrate maintaining variables.",
      topic: "FBA methods",
      sourceHint: "Functional analysis involves systematically manipulating environmental events thought to maintain problem behavior within an experimental design.",
    },
    {
      id: "Q2",
      question: "Which is best described as a DESCRIPTIVE FBA method?",
      options: [
        { label: "A", text: "Questionnaire from a parent" },
        { label: "B", text: "ABC recording in the natural environment" },
        { label: "C", text: "Functional analysis with multielement design" },
        { label: "D", text: "Trial-based functional analysis" },
      ],
      answer: "B",
      explanation: "Descriptive assessment includes ABC recording (continuous and narrative) and scatterplot recording.",
      topic: "Descriptive assessment",
      sourceHint: "Descriptive assessment involves observation of the problem behavior in relation to events that are not arranged in a systematic manner and includes ABC recording (both continuous and narrative) and scatterplot recording.",
    },
    {
      id: "Q3",
      question: "Why is the FORM of a behavior NOT a reliable guide to intervention?",
      options: [
        { label: "A", text: "Because topography varies day to day" },
        { label: "B", text: "Because the same form can be maintained by very different reinforcement contingencies" },
        { label: "C", text: "Because topography is hard to measure" },
        { label: "D", text: "Because topography requires permanent product measurement" },
      ],
      answer: "B",
      explanation: "Topography reveals little about function; identical-looking behaviors can serve very different functions.",
      topic: "Function vs topography",
      sourceHint: "The topography, or form, of a behavior often reveals little useful information about the conditions that account for it.",
    },
    {
      id: "Q4",
      question: "Replacement behaviors should be FUNCTIONALLY EQUIVALENT, meaning:",
      options: [
        { label: "A", text: "They look like the problem behavior" },
        { label: "B", text: "They produce the same reinforcer that maintained the problem behavior" },
        { label: "C", text: "They occur in the same setting" },
        { label: "D", text: "They are reinforced on the same schedule" },
      ],
      answer: "B",
      explanation: "Functional equivalence means producing the same maintaining reinforcer; only then will the replacement compete with the problem behavior.",
      topic: "Replacement behavior",
      sourceHint: "the replacement behavior should be functionally equivalent to the problem behavior (i.e., the replacement behavior should produce the same reinforcers that previously maintained the problem behavior).",
    },
    {
      id: "Q5",
      question: "Which is NOT one of the four FBA steps?",
      options: [
        { label: "A", text: "Gather information via indirect and descriptive assessment" },
        { label: "B", text: "Formulate hypotheses" },
        { label: "C", text: "Test hypotheses using functional analysis" },
        { label: "D", text: "Implement punishment if hypotheses are unclear" },
      ],
      answer: "D",
      explanation: "FBA reduces reliance on default (punishment-based) technologies. The four steps are gather, formulate hypotheses, test, and intervene based on function.",
      topic: "FBA process",
      sourceHint: "FBA can best be viewed as a four-step process: gather information via indirect and descriptive assessment, interpret information and formulate hypotheses, test hypotheses using functional analysis, develop intervention options based on the function.",
    },
  ],
  reviewPrompts: [
    "Walk through the four-step FBA process for a case you know.",
    "Pick between indirect, descriptive, and functional analysis for three different scenarios.",
    "Plan a function-based intervention using a functionally equivalent replacement.",
    "Defend the use of brief or trial-based functional analysis in a school setting.",
    "Explain why FBA reduces reliance on default technologies.",
  ],
  recap: [
    "FBA identifies the function (maintaining contingency) of a problem behavior. Three method families - indirect, descriptive, functional (experimental) analysis - form a continuum from easiest/least precise to most rigorous.",
    "The four-step process gathers, hypothesizes, tests, and intervenes. Functional analysis variations adapt the method to brief, natural, trial-based, latency-based, synthesized, and precursor-behavior contexts.",
    "Topography reveals little; function reveals everything intervention-relevant. Replacement behaviors must be functionally equivalent to compete with the problem behavior. FBA reduces reliance on default punishment-based technologies.",
  ],
  sourceExcerpts: [
    {
      heading: "Function vs topography",
      text: "The topography, or form, of a behavior often reveals little useful information about the conditions that account for it. Identifying the conditions that account for a behavior (its function) suggests what conditions need to be altered to change the behavior.",
    },
    {
      heading: "Three method families",
      text: "FBA methods can be classified into three types: (a) functional (experimental) analysis, (b) descriptive assessment, and (c) indirect assessment. The methods can be arranged on a continuum with respect to considerations such as ease of use and the type and precision of information they yield.",
    },
    {
      heading: "Four-step process",
      text: "FBA can best be viewed as a four-step process: First, gather information via indirect and descriptive assessment. Second, interpret information from indirect and descriptive assessment and formulate hypotheses about the purpose of problem behavior. Third, test hypotheses using functional analysis. Fourth, develop intervention options based on the function of problem behavior.",
    },
    {
      heading: "Functional equivalence",
      text: "When teaching an alternative behavior as a replacement for problem behavior, the replacement behavior should be functionally equivalent to the problem behavior (i.e., the replacement behavior should produce the same reinforcers that previously maintained the problem behavior).",
    },
  ],
};

export default chapter27Module;
